import { NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { RouterModule, Routes } from "@angular/router";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";

import "hammerjs";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { TranslateModule } from "@ngx-translate/core";
import { ToastrModule } from "ngx-toastr";

import { CoreModule } from "@core/core.module";
import { CoreCommonModule } from "@core/common.module";
import { CoreSidebarModule, CoreThemeCustomizerModule } from "@core/components";

import { coreConfig } from "app/app-config";
import { SharedModule } from "app/shared/shared.module";
import { ZerakiToastComponent } from "app/shared/components/zeraki-toast/zeraki-toast.component";

import { AppComponent } from "app/app.component";
import { LayoutModule } from "app/layout/layout.module";
import { HashLocationStrategy, LocationStrategy } from "@angular/common";
import { ErrorComponent } from "./main/pages/miscellaneous/error/error.component";
import { AuthGuard, ErrorInterceptor, JwtInterceptor } from "@core/helpers";
import { LandingPageComponent } from "./main/pages/landing-page/landing-page.component";
import { UpgradePageComponent } from "./main/pages/payment-page/upgrade-page.component";
import { CookieModule } from "ngx-cookie";
import { AdRedirectingPageComponent } from "./main/pages/ad-redirecting-page/ad-redirecting-page.component";
import { MathjaxModule } from "mathjax-angular";
import { JoinCahootNonLoggedInUsersComponent } from "./main/cahoot/components/join-cahoot-non-logged-in-users/join-cahoot-non-logged-in-users.component";
import { JoinViaCodeComponent } from "./main/cahoot/components/join-via-code/join-via-code.component";
import { MatDialogModule } from '@angular/material/dialog';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAnalytics, getAnalytics, ScreenTrackingService } from '@angular/fire/analytics';
import { MawinguRedirectComponent } from "./main/pages/mawingu-redirect/mawingu-redirect.component";
import { ExternalRedirectComponent } from "./main/pages/external-redirect/external-redirect.component";

const appRoutes: Routes = [
  {
    path: "dashboard",
    loadChildren: () =>
      import("./main/dashboards/dashboards.module").then(
        (m) => m.DashboardsModule
      ),
  },
  {
    path: "learn",
    loadChildren: () =>
      import("./main/learn/learn.module").then((m) => m.LearnModule),
  },
  {
    path: "careers",
    loadChildren: () =>
      import("./main/careers/careers.module").then((m) => m.CareersModule),
  },
  {
    path: "help",
    loadChildren: () =>
      import("./main/help/help.module").then((m) => m.HelpModule),
  },
  {
    path: "notes",
    loadChildren: () =>
      import("./main/notes/notes.module").then((m) => m.NotesModule),
  },
  {
    path: "assignments",
    loadChildren: () =>
      import("./main/assignments/assignments.module").then(
        (m) => m.AssignmentsModule
      ),
  },
  {
    path: "quiz",
    loadChildren: () =>
      import("./main/quiz/quiz.module").then((m) => m.QuizModule),
  },
  {
    path: "exams",
    loadChildren: () =>
      import("./main/exams/exams.module").then((m) => m.ExamsModule),
  },
  {
    path: "school",
    loadChildren: () =>
      import("./main/school/school.module").then((m) => m.MySchoolModule),
  },
  {
    path: "schools",
    loadChildren: () =>
      import("./main/schools/schools.module").then((m) => m.SchoolsModule),
  },
  {
    path: "upload",
    loadChildren: () =>
      import("./main/upload-content/upload-content.module").then(
        (m) => m.UploadContentModule
      ),
  },
  {
    path: "account-settings",
    loadChildren: () =>
      import("./main/account-settings/account-settings.module").then(
        (m) => m.AccountSettingsModule
      ),
  },
  {
    path: "pages",
    loadChildren: () =>
      import("./main/pages/pages.module").then((m) => m.PagesModule),
  },
  {
    path: "profile",
    loadChildren: () =>
      import("./main/pages/profile/profile.module").then(
        (m) => m.ProfileModule
      ),
  },
  {
    path: "notifications",
    canActivate: [AuthGuard],
    loadComponent: () =>
      import("./main/pages/notifications/notifications.component").then(
        (m) => m.NotificationsComponent
      ),
  },
  {
    path: "statistics",
    loadChildren: () =>
      import("./main/statistics/statistics.module").then(
        (m) => m.StatisticsModule
      ),
  },
  {
    path: "subscribed-users",
    loadChildren: () =>
      import(
        "./main/support-subscribed-users/support-subscribed-users.module"
      ).then((m) => m.SupportSubscribedUsersModule),
  },
  {
    path: "beewiz",
    loadChildren: () =>
      import("./main/cahoot/cahoot.module").then((m) => m.CahootModule),
  },
  {
    path: "bookmarks",
    loadChildren: () =>
      import("./main/bookmarks/bookmarks.module").then((m) => m.BookmarksModule),
  },
  {
    path: "zeraki-academy",
    loadChildren: () =>
      import("./main/learn/learn.module").then((m) => m.LearnModule),
  },
  {
    path: "onboarding",
    loadChildren: () =>
      import("./main/onboarding/onboarding.module").then((m) => m.OnboardingModule),
  },
  {
    path: "cbc",
    loadChildren: () =>
      import("./main/cbc/cbc.module").then((m) => m.CbcModule),
  },
  {
    path: "exam-practise",
    loadChildren: () =>
      import("./main/exam-practise/exam-practise.module").then((m) => m.ExamPractiseModule),
  },
  {
    path: "chat-bot",
    loadChildren: () =>
      import("./main/chat-bot/chat-bot.module").then((m) => m.ChatBotModule),
  },
  {
    path: "e-soma",
    loadChildren: () =>
      import("./main/e-soma/e-soma.module").then((m) => m.ESomaModule),
  },
  {
    path: "tryout",
    loadChildren: () =>
      import("./main/try-it-out/try-it-out.module").then((m) => m.TryItOutModule),
  },
  {
    path: "login",
    component: LandingPageComponent,
  },
  {
    path: "mawingu-redirect",
    component: MawinguRedirectComponent,
  },
  {
    path: "external-redirect",
    component: ExternalRedirectComponent,
  },
  {
    path: "logout",
    component: LandingPageComponent,
  },
  {
    path: "upgrade",
    component: UpgradePageComponent,
  },
  {
    path: "messages",
    component: AdRedirectingPageComponent,
  },
  {
    path: "join-contest",
    component: JoinCahootNonLoggedInUsersComponent
  },
  {
    path: "bee-wiz",
    component: JoinViaCodeComponent
  },
  {
    path: "xrtopdy",
    canActivate: [AuthGuard],
    component: AdRedirectingPageComponent,
    data: { source: "discount" },
  },
  {
    path: "pages/miscellaneous/error",
    component: ErrorComponent,
  },
  {
    path: "",
    redirectTo: "login",
    pathMatch: "full",
  },
  {
    path: "**",
    redirectTo: "/pages/miscellaneous/error", //Error 404 - Page not found
  },
];

export const coreConfig1 = {
  config: {
    loader: {
      load: [
        "input/tex-base",
        "output/svg",
        "ui/menu",
        "[tex]/require",
        "[tex]/ams",
        // Loads the actual code for \color{...}. Listing "color" under tex.packages below only
        // marks it active — without this the macro stays undefined and MathJax prints the
        // literal "\color" in red (its noundefined behaviour) followed by the colour name.
        "[tex]/color",
      ],
    },
    tex: {
      inlineMath: [
        ["$", "$"],
        ["\\(", "\\)"],
      ],
      // "[+]" adds to the bundle's own default package set (ams, newcommand, require, autoload,
      // configmacros, textmacros, noundefined) instead of replacing it — a plain array here
      // previously disabled autoload and the rest for the whole app.
      packages: { "[+]": ["color"] },
    },
    svg: { fontCache: "global" },
  },
  src: "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js",
};

const firebaseConfig = {
  apiKey: "AIzaSyBbZQGUMM74Xc38Xn-js5KaJPMLA42yjCw",
  authDomain: "zeraki-learning.firebaseapp.com",
  projectId: "zeraki-learning",
  storageBucket: "zeraki-learning.appspot.com",
  messagingSenderId: "506495537495",
  appId: "1:506495537495:web:ca37363ce3aba7c7da6e12",
  measurementId: "G-12889DBWKY"
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    provideFirebaseApp(() => initializeApp(firebaseConfig)),
    provideAnalytics(() => getAnalytics()),
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    RouterModule.forRoot(appRoutes, {
      scrollPositionRestoration: "enabled",
    }),
    TranslateModule.forRoot(),

    //NgBootstrap
    NgbModule,
    SharedModule,
    ToastrModule.forRoot({
      toastComponent: ZerakiToastComponent,
      positionClass: "toast-top-right",
      timeOut: 5000,
      extendedTimeOut: 1000,
      closeButton: false,
      tapToDismiss: true,
      progressBar: false,
      toastClass: "zeraki-toast-container",
      preventDuplicates: true,
    }),

    // Core modules
    CoreModule.forRoot(coreConfig),
    CoreCommonModule,
    CoreSidebarModule,
    CoreThemeCustomizerModule,

    // App modules
    LayoutModule,
    CookieModule.forRoot(),

    MathjaxModule.forRoot(coreConfig1),
    MatDialogModule,
  ],
  bootstrap: [AppComponent],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    ScreenTrackingService,
  ],
})
export class AppModule {}
